import React, { useState, useEffect } from "react";
import { PinInput, PinInputField } from "@UI/Components/PinInput/PinInput";

import {
  Wizard,
  WizardHeader,
  WizardContent,
} from "@Shared/Components/Layouts/Wizard/Wizard";

import { Box } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import { Container } from "@UI/Components/Container/Container";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { HStack, VStack } from "@UI/Components/Stack/Stack";

import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { useToast } from "@UI/hooks/useToast";
import { AlertBox } from "@UI/Components/AlertBox/AlertBox";
import { Icon } from "@UI/Components/Icon/Icon";
import { Xmark } from "@UI/Components/Icons/Xmark";
import i18n from "@I18n/services/i18n";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import messages from "../messages";

import { Spinner } from "@UI/Components/Spinner/Spinner";
import {
  sendVerificationCode,
  verifyCode,
} from "../services/phoneVerification";

const RESEND_COOLDOWN_S = 60;
const RESEND_COOLDOWN_KEY = "rt.phoneVerification.resendCooldownExpiry";
const SMS_SENT_KEY = "rt.phoneVerification.smsSent";
const LAST_SENT_PHONE_KEY = "rt.phoneVerification.lastSentPhone";

const formatCountdown = (seconds: number) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

type AlertStatus = "idle" | "resent" | "wrong_code" | "max_attempts";

export const PhoneNumberCodeStep: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const rawCallbackUrl = useSearchParams("callbackUrl");
  const callbackUrl = rawCallbackUrl
    ? rawCallbackUrl.startsWith("/") ? rawCallbackUrl : `/${rawCallbackUrl}`
    : null;

  useEffect(() => {
    if (!sessionStorage.getItem(SMS_SENT_KEY)) {
      navigate("PHONE_NUMBER_VERIFICATION", { params: { step: "form" } });
    }
  }, []);

  const [code, setCode] = useState("");
  const [alertStatus, setAlertStatus] = useState<AlertStatus>("idle");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState<number>(() => {
    const stored = sessionStorage.getItem(RESEND_COOLDOWN_KEY);
    if (stored) {
      const remaining = Math.round((Number(stored) - Date.now()) / 1000);
      if (remaining > 0) return remaining;
    }
    return 0;
  });

  // Countdown timer — starts immediately since the code was just sent from step 1
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = async () => {
    if (code.length !== 4 || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await verifyCode(code);
      toast({
        status: "success",
        position: "top-right",
        duration: 2000,
        isClosable: true,
        render: () => (
          <AlertBox
            status="success"
            background="white"
            childrenContainerStyle={{ justifyContent: "start" }}
            marginTop="80px"
          >
            <HStack justifyContent="space-between" flexGrow={1}>
              <Text m="0" ml={3} color="gray.700">
                {i18n.formatMessage(messages["verification.phoneNumberVerification.success_toast"])}
              </Text>
              <Icon as={Xmark} color="gray.700" cursor="pointer" onClick={() => toast.closeAll()} />
            </HStack>
          </AlertBox>
        ),
      });
      setTimeout(() => {
        sessionStorage.removeItem(SMS_SENT_KEY);
        sessionStorage.removeItem(RESEND_COOLDOWN_KEY);
        sessionStorage.removeItem(LAST_SENT_PHONE_KEY);
        window.location.href = callbackUrl ?? "/dashboard";
      }, 1500);
    } catch (error: unknown) {
      const errorKey = (error as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      if (errorKey === "too_many_retries") {
        setAlertStatus("max_attempts");
      } else {
        setAlertStatus("wrong_code");
      }
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    const lastPhone = sessionStorage.getItem(LAST_SENT_PHONE_KEY);
    if (cooldown > 0 || isResending || !lastPhone) return;
    setIsResending(true);
    try {
      await sendVerificationCode(lastPhone);
      sessionStorage.setItem(RESEND_COOLDOWN_KEY, String(Date.now() + RESEND_COOLDOWN_S * 1000));
      setAlertStatus("resent");
      setCooldown(RESEND_COOLDOWN_S);
      setCode("");
    } catch (error: unknown) {
      const errorKey = (error as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      if (errorKey === "too_many_retries") {
        setAlertStatus("max_attempts");
      }
    } finally {
      setIsResending(false);
    }
  };

  const isError = alertStatus === "wrong_code";

  return (
    <Wizard
      paddingTop={{ base: 5, md: 8 }}
      maxW={{
        base: "container.sm",
        sm: "container.sm",
        lg: "container.sm",
      }}
    >
      <WizardHeader marginBottom={4}>
        <Container textAlign="start" px={{ base: 0 }}>
          <VStack alignItems="start" spacing={2}>
            <Heading fontSize={{ base: "3xl", lg: "5xl" }}>
              <FormattedMessage
                id={"verification.phoneNumberVerification.code_title"}
                defaultMessage={"Verify your phone number"}
              />
            </Heading>
            <Text>
              <FormattedMessage
                id={
                  "verification.phoneNumberVerification.phone_code_description"
                }
                defaultMessage={
                  "Enter the 4-digit code sent to your phone to verify your number. Valid for 1 minute."
                }
              />
            </Text>
          </VStack>
        </Container>
      </WizardHeader>
      <WizardContent>
        <Box borderRadius="lg" padding={8} bg="white">
          <VStack spacing={6} alignItems="stretch">
            {/* Top status message */}
            {alertStatus === "resent" && (
              <Text
                color="green.500"
                textAlign="center"
                fontWeight="medium"
                fontSize="sm"
                mb={0}
              >
                <FormattedMessage
                  id={"verification.phoneNumberVerification.code_resent"}
                  defaultMessage={"Code sent again! Check your SMS."}
                />
              </Text>
            )}
            {isError && (
              <Text color="red.500" textAlign="center" fontWeight="medium" fontSize="sm" mb={0}>
                <FormattedMessage
                  id={"verification.phoneNumberVerification.wrong_code"}
                  defaultMessage={"Incorrect code. Please try again."}
                />
              </Text>
            )}

            {/* OTP inputs */}
            <HStack
              justifyContent="center"
              spacing={3}
              onKeyDown={(e) => { if (e.key === "Enter" && code.length === 4) handleVerify(); }}
            >
              <PinInput
                autoFocus
                value={code}
                onChange={(val) => {
                  setCode(val);
                  if (isError) setAlertStatus("idle");
                }}
                isInvalid={isError}
                otp
                placeholder=""
              >
                <PinInputField
                  w="64px"
                  h="64px"
                  fontSize="2xl"
                  fontWeight="bold"
                  borderRadius="12px"
                />
                <PinInputField
                  w="64px"
                  h="64px"
                  fontSize="2xl"
                  fontWeight="bold"
                  borderRadius="12px"
                />
                <PinInputField
                  w="64px"
                  h="64px"
                  fontSize="2xl"
                  fontWeight="bold"
                  borderRadius="12px"
                />
                <PinInputField
                  w="64px"
                  h="64px"
                  fontSize="2xl"
                  fontWeight="bold"
                  borderRadius="12px"
                />
              </PinInput>
            </HStack>

            {/* Verify button */}
            <Button
              colorScheme="seaGreen"
              size="lg"
              w="100%"
              maxW="300px"
              alignSelf="center"
              position="relative"
              onClick={handleVerify}
              isDisabled={code.length !== 4 || isSubmitting}
            >
              <Box as="span" opacity={isSubmitting ? 0 : 1}>
                <FormattedMessage
                  id={"verification.phoneNumberVerification.verify"}
                  defaultMessage={"Verify"}
                />
              </Box>
              {isSubmitting && <Spinner size="sm" position="absolute" />}
            </Button>

            {/* Footer: max_attempts | cooldown timer | resend link */}
            {alertStatus === "max_attempts" ? (
              <HStack justifyContent="center" flexWrap="wrap" spacing={1} maxW="400px" alignSelf="center">
                <Text fontSize="sm" color="gray.700" mb={0}>
                  <FormattedMessage
                    id={
                      "verification.phoneNumberVerification.max_attempts_message"
                    }
                    defaultMessage={
                      "You have reached your maximum number of attempts."
                    }
                  />
                </Text>
                <Box as="a" href="/contact" color="pink.400" textDecoration="underline" fontSize="sm">
                  <FormattedMessage
                    id={"verification.phoneNumberVerification.contact_support"}
                    defaultMessage={"Please contact support"}
                  />
                </Box>
              </HStack>
            ) : cooldown > 0 ? (
              <Text textAlign="center" fontSize="sm" color="gray.500" maxW="400px" alignSelf="center">
                <FormattedMessage
                  id={"verification.phoneNumberVerification.next_try"}
                  defaultMessage={"Next try: {time}"}
                  values={{ time: formatCountdown(cooldown) }}
                />
              </Text>
            ) : (
              <HStack justifyContent="center" flexWrap="wrap" spacing={1}>
                <Text fontSize="sm" color="gray.700" mb={0}>
                  <FormattedMessage
                    id={
                      "verification.phoneNumberVerification.resend_prompt"
                    }
                    defaultMessage={"Didn't receive the code?"}
                  />
                </Text>
                <Box
                  as="span"
                  fontSize="sm"
                  color="pink.400"
                  fontWeight="medium"
                  cursor={isResending ? "default" : "pointer"}
                  onClick={handleResend}
                  _hover={{ textDecoration: "underline" }}
                >
                  <FormattedMessage
                    id={"verification.phoneNumberVerification.resend_code"}
                    defaultMessage={"Resend code"}
                  />
                </Box>
              </HStack>
            )}
          </VStack>
        </Box>
      </WizardContent>
    </Wizard>
  );
};
